/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { values, cloneDeep } from 'lodash';
import { Warning2 } from 'iconsax-react';
import { confirmReallocateMessage } from 'constants/messages';
import endpoints from 'constants/endpoints';
import { useModal } from 'hooks/useModal';
import useApi from 'hooks/useApi';
import useFormHandler from 'hooks/useFormHandler';
import useTable from 'hooks/useTable';
import { Button, DynamicForm, Table, Input, Alert } from 'components';
import { ModalsList, Props } from 'types/modals';
import { TableOptions, ComponentList, FilterTypes } from 'types/table';
import { TextInputTypes } from 'types/dynamicInputs';
import Base from '../../base';
import schema from './schema';
import { removeSeconds } from 'utils/time';

const registeredTableOptions: TableOptions = {
  tableKey: 'allocations',
  columns: [
    {
      id: 'check_or_warning',
      header: ' ',
      type: ComponentList.CHECK_OR_WARNING,
      width: '32px'
    },
    { id: 'allocation_status', header: 'Allocated', type: ComponentList.ALLOCATION_STATUS, width: '160px' },
    {
      id: 'alcohol_certification_completed',
      header: 'Alcohol Certification',
      type: ComponentList.YES_OR_NO,
      width: '180px'
    },
    {
      id: 'backgroundCheckStatus',
      header: 'Background Check',
      type: ComponentList.BACKGROUND_CHECK_STATUS,
      width: '180px',
      filter: { key: 'background_check_completed', type: FilterTypes.MULTI }
    },
    {
      id: 'has_completed_video_interview',
      header: 'Video Interview',
      type: ComponentList.YES_OR_NO,
      width: '140px'
    },
    { id: 'user_id', header: 'ID #', type: ComponentList.TEXT, width: '110px' },
    { id: 'legal_first_name', header: 'First name', type: ComponentList.TEXT, width: '140px' },
    { id: 'legal_last_name', header: 'Last name', type: ComponentList.TEXT, width: '140px' },
    { id: 'email', header: 'Email', type: ComponentList.TEXT },
    { id: 'age', header: 'Age', type: ComponentList.TEXT, width: '110px' },
    { id: 'num_events_worked', header: '# Events Worked', type: ComponentList.TEXT, width: '110px' },
    { id: 'num_lead_events_worked', header: '# Lead Events Worked', type: ComponentList.TEXT, width: '130px' },
    { id: 'last_event_worked', header: 'Last Event Worked', type: ComponentList.DATE, width: '110px' },
    { id: 'email', header: 'Email', type: ComponentList.TEXT },
    {
      id: 'onboardingComplete',
      header: 'Onboarding',
      type: ComponentList.YES_OR_NO,
      width: '130px',
      filter: { key: 'onboarding_completed', type: FilterTypes.MULTI }
    }
  ],
  sort: {
    column: 'id',
    order: 'asc'
  },
  itemsPerPage: 100,
  hideFooter: true
};

const allocatedTableOptions: TableOptions = cloneDeep(registeredTableOptions);

enum FilterBy {
  'REGISTERED' = 'registered',
  'ALLOCATED' = 'allocated'
}

const filterFc =
  (search: string) =>
  ({ legal_first_name, legal_last_name, email = '', user_id }: any) => {
    const aux = {
      legal_first_name: legal_first_name.toLowerCase(),
      legal_last_name: legal_last_name.toLowerCase(),
      email,
      user_id: user_id?.toString() || ''
    };
    return values(aux).some(el => el.includes(search.toLowerCase()));
  };

const AllocationsModal = ({ closing, onClose, params }: Props) => {
  const modal = useModal();
  const api = useApi();
  const formHandler = useFormHandler(schema);
  const [search, setSearch] = useState('');
  const [filterBy, setFilterBy] = useState(FilterBy.REGISTERED);
  const [isExceeded, setIsExceeded] = useState(false);
  const items = api.response?.items || [];

  const clickedLocationPositionId = params.locationPositionId ? Number(params.locationPositionId) : null;
  const hasSameLocationPosition = (item: any) =>
    clickedLocationPositionId !== null && Number(item.location_position_id) === clickedLocationPositionId;

  const allocatedWorkers = items.filter(
    (item: any) => item.allocation_status === 'Allocated' && hasSameLocationPosition(item)
  );

  // Initialize selected as empty array
  const [selected, setSelected] = useState<any[]>([]);

  // Auto-populate selected with workers assigned to the clicked stand position.
  useEffect(() => {
    if (items.length > 0) {
      const workersWithSamePosition = items.filter(
        (item: any) =>
          clickedLocationPositionId !== null && Number(item.location_position_id) === clickedLocationPositionId
      );
      setSelected(workersWithSamePosition);
    }
  }, [items, clickedLocationPositionId]);

  const allocatedTableHandler = useTable({
    alternative: true,
    options: {
      ...allocatedTableOptions,
      data: search ? allocatedWorkers.filter(filterFc(search)) : allocatedWorkers
    }
  });

  const registeredWorkers = search ? items.filter(filterFc(search)) : items;

  const registeredTableHandler = useTable({
    alternative: true,
    options: {
      ...registeredTableOptions,
      data: registeredWorkers,
      selectAll: true
    },
    selected: { value: selected, change: setSelected }
  });

  const handleSave = () => {
    formHandler.setIsSubmitting(true);
    const employees = selected.map((item: any) => +item.id);

    const assignedFlag = params.checkAlreadyAssigned(
      'staff',
      selected.map((item: any) => item.id)
    );
    const data = {
      employees,
      workersNeeded: formHandler.data.workersNeeded,
      reportTime: formHandler.data.reportTime
    };

    if (assignedFlag) {
      modal.open(
        ModalsList.CONFIRM_MODAL,
        flag => {
          flag ? onClose(data) : formHandler.setIsSubmitting(false);
        },
        confirmReallocateMessage
      );
    } else {
      onClose(data);
    }
  };

  const handleRemove = () => {
    params.handleRemove();
    onClose();
  };

  useEffect(() => {
    formHandler.setFullForm({
      workersNeeded: params.workersNeeded,
      reportTime:
        params.positionsWithReportTimes && params.locationPositionId
          ? removeSeconds(
              params.positionsWithReportTimes.find((p: any) => p.locationPositionId === params.locationPositionId)
                ?.reportTime || ''
            )
          : ''
    });
    api.fetchData(endpoints.GET_EVENT_SUBSCRIBED.get({ id: params.eventId, paginated: false }), {});
  }, []);

  useEffect(() => {
    const diff = params.workersNeeded - formHandler.data.workersNeeded + params.positionCapacity;
    if (Math.sign(diff) === -1) {
      setIsExceeded(true);
    } else {
      setIsExceeded(false);
    }
  }, [formHandler.data.workersNeeded, params.positionCapacity]);

  let reportTime = 'N/A';
  if (params.positionsWithReportTimes && params.locationPositionId) {
    const found = params.positionsWithReportTimes.find((p: any) => p.locationPositionId === params.locationPositionId);
    if (found && found.reportTime) {
      reportTime = found.reportTime;
    }
  }

  return (
    <Base
      className="modal-table allocations-modal"
      header={
        <div className="search-header">
          <h6>
            {params.standName} - {params.positionName}
          </h6>
          <Input type={TextInputTypes.TEXT} placeholder="Search employees.." value={search} onChange={setSearch} />
        </div>
      }
      closing={closing}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <Button text="Remove position" onClick={handleRemove} variant="secondary" />
          <Button text="Save changes" onClick={handleSave} disabled={!formHandler.valid || formHandler.isSubmitting} />
        </>
      }
    >
      <div className="form">
        <DynamicForm handler={formHandler} />

        {isExceeded && (
          <Alert
            type="warning"
            icon={<Warning2 size={20} />}
            message="The total number of positions exceeds the number of positions available"
          />
        )}
      </div>

      <div className="buttons">
        <Button
          text="All workers registered"
          small
          variant={filterBy === FilterBy.REGISTERED ? 'primary' : 'secondary'}
          onClick={() => setFilterBy(FilterBy.REGISTERED)}
        />
        <Button
          text="Allocated workers"
          variant={filterBy === FilterBy.REGISTERED ? 'secondary' : 'primary'}
          small
          onClick={() => setFilterBy(FilterBy.ALLOCATED)}
        />
      </div>

      <Table handler={filterBy === FilterBy.REGISTERED ? registeredTableHandler : allocatedTableHandler} />
    </Base>
  );
};

export default AllocationsModal;
